'use client'

import { useCallback, useLayoutEffect, useRef } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { useUserContext } from '@/context/userContext.shared'
import { useSelectedTeam } from '@/hooks/useSelectedTeam'
import { useUserUpdateStrict } from '@/services/user/user.mutation'
import { useUser } from '@/services/user/user.query'
import { pref, type Preference, type Prefs, setPref } from '@/utils/pref'

const DEFAULT_TEAM_PREFERENCE_KEY = 'user-default-team-id'

type ProductCreateScopeUser = {
  id?: string | null
  preferences?: Prefs
}

type RefreshAuthSession = (options: { forceRefresh: true }) => Promise<{
  tokens?: { idToken?: unknown }
}>

type UpdateUserPreferences = (input: {
  id: string
  preferences: Preference[]
}) => Promise<unknown>

export type ProductCreateOrganizationScope = {
  organizationID?: string | null
  organizationMasterID?: string | null
}

type CurrentProductCreateScope = {
  activeOrganizationID?: string
  workspaceOrganizationID?: string
  activeOrganizationMasterID?: string
  scopeKind?: string
  user?: ProductCreateScopeUser
  isImpersonating: boolean
  sessionScopeKey: string
}

export class ProductCreateAuthorizerScopeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ProductCreateAuthorizerScopeError'
  }
}

export async function ensureProductCreateAuthorizerScope({
  organizationID,
  organizationMasterID,
  activeOrganizationID,
  activeOrganizationMasterID,
  scopeKind,
  user,
  isImpersonating,
  updateUserPreferences,
  refreshAuthSession,
  assertScopeIsCurrent,
}: {
  organizationID?: string | null
  organizationMasterID?: string | null
  activeOrganizationID?: string
  activeOrganizationMasterID?: string
  scopeKind?: string
  user?: ProductCreateScopeUser
  isImpersonating: boolean
  updateUserPreferences: UpdateUserPreferences
  refreshAuthSession: RefreshAuthSession
  assertScopeIsCurrent?: () => void
}): Promise<boolean> {
  // Preserve the existing impersonation contract. Its authorization context is
  // owned by the impersonation flow, not by the impersonated user's preference.
  if (!organizationID || isImpersonating) return false

  if (scopeKind !== 'organization' || organizationID !== activeOrganizationID) {
    throw new ProductCreateAuthorizerScopeError(
      'The product organization does not match the active organization',
    )
  }

  if (
    organizationMasterID &&
    organizationMasterID !== activeOrganizationMasterID
  ) {
    throw new ProductCreateAuthorizerScopeError(
      'The product organization master does not match the active organization',
    )
  }

  if (!user?.id) {
    throw new ProductCreateAuthorizerScopeError(
      'The user is unavailable while preparing product creation',
    )
  }

  const preferredOrganizationID = pref(
    user.preferences,
    DEFAULT_TEAM_PREFERENCE_KEY,
  )
  if (preferredOrganizationID !== organizationID) {
    await updateUserPreferences({
      id: user.id,
      preferences: setPref(
        user.preferences,
        DEFAULT_TEAM_PREFERENCE_KEY,
        organizationID,
      ),
    })
  }

  // Preference persistence is asynchronous. Do not mint a token for the
  // original organization if the user switched scope while that write was in
  // flight. The hook keeps a second guard after the refresh as well.
  assertScopeIsCurrent?.()

  // The standard AppSync authorizer context is cached by token. Refresh before
  // the first create in this scope so the dual Product/ProductStatus mutation
  // cannot start with a stale organization context.
  const session = await refreshAuthSession({ forceRefresh: true })
  if (!session.tokens?.idToken) {
    throw new ProductCreateAuthorizerScopeError(
      'The authenticated session could not be refreshed',
    )
  }

  return true
}

export function usePrepareProductCreateAuthorizerScope() {
  const { user, organizationScope } = useUserContext()
  const { setSelectedTeam } = useSelectedTeam()
  const { isImpersonating, sessionScopeKey } = useSessionRuntime()
  const { mutateAsync: updateUserPreferences } = useUserUpdateStrict()
  const { refetch: refetchUser } = useUser()
  const consumerGenerationRef = useRef(0)
  const scopePreparationsInFlightRef = useRef(new Map<string, Promise<void>>())
  const currentScopeRef = useRef<CurrentProductCreateScope>({
    activeOrganizationID: organizationScope.activeOrganizationId,
    workspaceOrganizationID: organizationScope.workspaceOrganizationId,
    activeOrganizationMasterID: organizationScope.activeOrganizationMasterId,
    scopeKind: organizationScope.scopeKind,
    user,
    isImpersonating,
    sessionScopeKey,
  })
  useLayoutEffect(() => {
    const generation = consumerGenerationRef.current + 1
    consumerGenerationRef.current = generation
    return () => {
      if (consumerGenerationRef.current === generation) {
        consumerGenerationRef.current = generation + 1
      }
      scopePreparationsInFlightRef.current.clear()
    }
  }, [])
  useLayoutEffect(() => {
    currentScopeRef.current = {
      activeOrganizationID: organizationScope.activeOrganizationId,
      workspaceOrganizationID: organizationScope.workspaceOrganizationId,
      activeOrganizationMasterID: organizationScope.activeOrganizationMasterId,
      scopeKind: organizationScope.scopeKind,
      user,
      isImpersonating,
      sessionScopeKey,
    }
  }, [
    isImpersonating,
    organizationScope.activeOrganizationId,
    organizationScope.workspaceOrganizationId,
    organizationScope.activeOrganizationMasterId,
    organizationScope.scopeKind,
    sessionScopeKey,
    user,
  ])
  return useCallback(
    async ({
      organizationID: requestedOrganizationID,
      organizationMasterID,
    }: ProductCreateOrganizationScope): Promise<string | undefined> => {
      const organizationID =
        requestedOrganizationID ??
        (currentScopeRef.current.scopeKind === 'solo'
          ? currentScopeRef.current.workspaceOrganizationID
          : undefined)
      if (!organizationID || currentScopeRef.current.isImpersonating)
        return undefined
      const consumerGeneration = consumerGenerationRef.current

      const assertCurrentScope = (): CurrentProductCreateScope => {
        if (consumerGenerationRef.current !== consumerGeneration) {
          throw new ProductCreateAuthorizerScopeError(
            'The product creation scope is no longer active',
          )
        }
        const currentScope = currentScopeRef.current
        if (
          currentScope.isImpersonating ||
          !(
            (currentScope.scopeKind === 'organization' &&
              currentScope.activeOrganizationID === organizationID) ||
            (currentScope.scopeKind === 'solo' &&
              currentScope.workspaceOrganizationID === organizationID &&
              !organizationMasterID)
          ) ||
          (organizationMasterID &&
            currentScope.activeOrganizationMasterID !== organizationMasterID)
        ) {
          throw new ProductCreateAuthorizerScopeError(
            'The active organization changed while preparing product creation',
          )
        }
        return currentScope
      }

      const currentScope = assertCurrentScope()
      const scopeKey = `${currentScope.sessionScopeKey}:${currentScope.user?.id ?? 'missing-user'}:${organizationID}:${organizationMasterID ?? 'no-master'}`
      const scopePreparationsInFlight = scopePreparationsInFlightRef.current

      let preparation = scopePreparationsInFlight.get(scopeKey)
      if (!preparation) {
        preparation = (async () => {
          if (assertCurrentScope().scopeKind === 'solo') {
            const selected = await setSelectedTeam(organizationID, {
              cookieValue: 'solo',
              forceScopeAlignment: true,
            })
            assertCurrentScope()
            if (!selected) {
              throw new ProductCreateAuthorizerScopeError(
                'The personal workspace could not be aligned for product creation',
              )
            }
            return
          }
          // The preference is shared across devices while the selected-team
          // cookie is local to this browser. Re-read the owning user record
          // for each scope preparation so a cached matching value cannot hide
          // a remote organization change.
          const freshUserResult = await refetchUser({ throwOnError: true })
          const freshUser = freshUserResult.data?.user
          const liveScope = assertCurrentScope()
          if (!freshUser?.id || freshUser.id !== liveScope.user?.id) {
            throw new ProductCreateAuthorizerScopeError(
              'The current user changed while preparing product creation',
            )
          }

          await ensureProductCreateAuthorizerScope({
            organizationID,
            organizationMasterID,
            activeOrganizationID: liveScope.activeOrganizationID,
            activeOrganizationMasterID: liveScope.activeOrganizationMasterID,
            scopeKind: liveScope.scopeKind,
            user: freshUser,
            isImpersonating: liveScope.isImpersonating,
            updateUserPreferences,
            refreshAuthSession: fetchAuthSession,
            assertScopeIsCurrent: () => {
              assertCurrentScope()
            },
          })
        })()
        scopePreparationsInFlight.set(scopeKey, preparation)
        const clearPreparation = () => {
          if (scopePreparationsInFlight.get(scopeKey) === preparation) {
            scopePreparationsInFlight.delete(scopeKey)
          }
        }
        void preparation.then(clearPreparation, clearPreparation)
      }

      await preparation
      assertCurrentScope()
      return organizationID
    },
    [refetchUser, updateUserPreferences, setSelectedTeam],
  )
}
